import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Trash2, KeyRound, Save, X, Shield, Pencil } from "lucide-react";
import { PageShell } from "@/components/SidebarLayout";
import { useAuth } from "@/auth/AuthContext";
import { api, formatApiError } from "@/lib/api";

const ROLES = [
  { key: "admin",       label: "Admin",       hint: "Full access — everything" },
  { key: "liaisoning",  label: "Liaisoning",  hint: "Edits Development Cost" },
  { key: "marketing",   label: "Marketing",   hint: "Edits Marketing Expenses" },
  { key: "epc",         label: "EPC",         hint: "Edits Construction (EPC)" },
  { key: "hr",          label: "HR",          hint: "Edits Salaries & HR" },
  { key: "finance",     label: "Finance",     hint: "Edits Statutory & Corpus + Loans" },
];

function RoleBadge({ role }) {
  const meta = ROLES.find((r) => r.key === role) || { label: role };
  return (
    <span
      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest font-semibold"
      style={{ background: "var(--cf-accent-soft)", color: "var(--cf-accent-deep)" }}
    >
      <Shield size={10} /> {meta.label}
    </span>
  );
}

export default function UserRoles() {
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ name: "", email: "", password: "", role: "liaisoning" });
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const [resetFor, setResetFor] = useState(null);
  const [resetPwd, setResetPwd] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error("Failed to load users", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  const createUser = async () => {
    if (!draft.name.trim() || !draft.email.trim() || !draft.password.trim()) {
      toast.error("All fields required");
      return;
    }
    try {
      await api.post("/users", draft);
      toast.success(`Added ${draft.name}`);
      setDraft({ name: "", email: "", password: "", role: "liaisoning" });
      setCreating(false);
      load();
    } catch (e) {
      toast.error("Create failed", { description: formatApiError(e.response?.data?.detail) });
    }
  };

  const startEdit = (u) => { setEditingId(u.id); setEditDraft({ name: u.name, role: u.role }); };
  const saveEdit = async () => {
    try {
      await api.put(`/users/${editingId}`, editDraft);
      toast.success("Updated");
      setEditingId(null);
      load();
    } catch (e) {
      toast.error("Update failed", { description: formatApiError(e.response?.data?.detail) });
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete ${u.email}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error("Delete failed", { description: formatApiError(e.response?.data?.detail) });
    }
  };

  const resetPassword = async () => {
    if (!resetPwd.trim()) { toast.error("Password can't be blank"); return; }
    try {
      await api.post(`/users/${resetFor.id}/reset-password`, { password: resetPwd });
      toast.success(`Password reset for ${resetFor.email}`);
      setResetFor(null);
      setResetPwd("");
    } catch (e) {
      toast.error("Reset failed", { description: formatApiError(e.response?.data?.detail) });
    }
  };

  if (!isAdmin) {
    return (
      <PageShell testId="user-roles-page" kicker="System" title="User Roles & Access" hideProjectFilter>
        <div className="cf-card p-8 text-center">
          <Shield size={32} className="mx-auto text-muted-light mb-3" />
          <div className="font-semibold text-ink">Admin access required</div>
          <p className="text-sm text-muted-dark mt-1">Only Admin users can manage roles.</p>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell
      testId="user-roles-page"
      kicker="System"
      title="User Roles & Access"
      hideProjectFilter
      actions={
        !creating && (
          <button
            data-testid="user-roles-add-btn"
            onClick={() => setCreating(true)}
            className="cf-touch inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-ink text-white text-sm font-medium hover:opacity-90"
          >
            <UserPlus size={14} /> Add user
          </button>
        )
      }
    >
      <p className="-mt-2 text-muted-dark text-sm max-w-3xl">
        Manage which team members can log in and what they can edit. Every user can view every page —
        edit access is scoped by role.
      </p>

      {/* Role legend */}
      <div className="cf-card p-4 md:p-5">
        <div className="text-[10px] uppercase tracking-widest text-muted-light font-semibold mb-3">Role reference</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {ROLES.map((r) => (
            <div key={r.key} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-surface/60">
              <RoleBadge role={r.key} />
              <span className="text-xs text-muted-dark">{r.hint}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Create form */}
      {creating && (
        <div className="cf-card p-5 cf-fade-up">
          <div className="text-[10px] uppercase tracking-widest text-muted-light font-semibold mb-3">Add user</div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <input
              data-testid="user-roles-new-name"
              placeholder="Full name"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              className="px-3 py-2 bg-surface rounded-lg text-sm"
            />
            <input
              data-testid="user-roles-new-email"
              placeholder="email@radiance.co.in"
              value={draft.email}
              onChange={(e) => setDraft({ ...draft, email: e.target.value })}
              className="px-3 py-2 bg-surface rounded-lg text-sm"
            />
            <input
              data-testid="user-roles-new-password"
              placeholder="Password"
              type="password"
              value={draft.password}
              onChange={(e) => setDraft({ ...draft, password: e.target.value })}
              className="px-3 py-2 bg-surface rounded-lg text-sm"
            />
            <select
              data-testid="user-roles-new-role"
              value={draft.role}
              onChange={(e) => setDraft({ ...draft, role: e.target.value })}
              className="px-3 py-2 bg-surface rounded-lg text-sm"
            >
              {ROLES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <button
              data-testid="user-roles-new-cancel"
              onClick={() => { setCreating(false); setDraft({ name: "", email: "", password: "", role: "liaisoning" }); }}
              className="px-3 py-2 text-xs text-muted-dark"
            >
              Cancel
            </button>
            <button
              data-testid="user-roles-new-submit"
              onClick={createUser}
              className="cf-touch inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-ink text-white text-sm font-medium hover:opacity-90"
            >
              <Save size={13} /> Create
            </button>
          </div>
        </div>
      )}

      {/* User list */}
      <div className="cf-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-muted-light border-b border-hairline/70">
                <th className="text-left font-semibold px-5 py-3">Name</th>
                <th className="text-left font-semibold px-3 py-3">Email</th>
                <th className="text-left font-semibold px-3 py-3">Role</th>
                <th className="text-right font-semibold px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={4} className="px-5 py-8 text-center text-sm text-muted-dark">Loading…</td></tr>
              )}
              {!loading && users.length === 0 && (
                <tr><td colSpan={4} className="px-5 py-8 text-center text-sm text-muted-dark">No users yet.</td></tr>
              )}
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.email}`} className="border-b border-hairline/50 last:border-0 hover:bg-surface-2/70 transition-colors">
                  <td className="px-5 py-3 text-sm font-medium text-ink">
                    {editingId === u.id ? (
                      <input
                        value={editDraft.name || ""}
                        onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })}
                        className="px-2 py-1 bg-surface rounded text-sm w-full"
                      />
                    ) : u.name}
                  </td>
                  <td className="px-3 py-3 text-sm text-muted-dark">{u.email}</td>
                  <td className="px-3 py-3">
                    {editingId === u.id ? (
                      <select
                        value={editDraft.role || u.role}
                        onChange={(e) => setEditDraft({ ...editDraft, role: e.target.value })}
                        className="px-2 py-1 bg-surface rounded text-sm"
                      >
                        {ROLES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                      </select>
                    ) : (
                      <RoleBadge role={u.role} />
                    )}
                  </td>
                  <td className="px-5 py-3 text-right text-sm">
                    {editingId === u.id ? (
                      <div className="flex justify-end gap-2">
                        <button data-testid={`user-save-${u.email}`} onClick={saveEdit} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-ink text-white text-xs font-semibold"><Save size={12} /> Save</button>
                        <button onClick={() => setEditingId(null)} className="px-3 py-1.5 text-xs text-muted-dark hover:text-ink">Cancel</button>
                      </div>
                    ) : (
                      <div className="flex justify-end items-center gap-1">
                        <button
                          data-testid={`user-edit-${u.email}`}
                          onClick={() => startEdit(u)}
                          title="Edit"
                          className="p-1.5 rounded-md text-muted-dark hover:text-ink hover:bg-surface-2 transition-colors"
                        ><Pencil size={13} strokeWidth={2} /></button>
                        <button
                          data-testid={`user-reset-${u.email}`}
                          onClick={() => setResetFor(u)}
                          title="Reset password"
                          className="p-1.5 rounded-md text-muted-dark hover:text-ink hover:bg-surface-2 transition-colors"
                        ><KeyRound size={13} strokeWidth={2} /></button>
                        {u.email !== me.email && (
                          <button
                            data-testid={`user-delete-${u.email}`}
                            onClick={() => deleteUser(u)}
                            title="Delete"
                            className="p-1.5 rounded-md text-muted-dark hover:text-red-600 hover:bg-red-50 transition-colors"
                          ><Trash2 size={13} strokeWidth={2} /></button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Reset PW modal */}
      {resetFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="cf-card p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <div className="font-display text-lg text-ink font-bold">Reset password</div>
              <button onClick={() => { setResetFor(null); setResetPwd(""); }} className="text-muted-dark hover:text-ink"><X size={18} /></button>
            </div>
            <p className="text-sm text-muted-dark mb-3">Set a new password for <span className="font-semibold text-ink">{resetFor.email}</span>.</p>
            <input
              data-testid="user-reset-pw-input"
              type="password"
              autoFocus
              placeholder="New password"
              value={resetPwd}
              onChange={(e) => setResetPwd(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && resetPassword()}
              className="w-full px-3 py-2 bg-surface rounded-lg text-sm"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => { setResetFor(null); setResetPwd(""); }} className="px-3 py-2 text-xs text-muted-dark">Cancel</button>
              <button
                data-testid="user-reset-pw-submit"
                onClick={resetPassword}
                className="cf-touch inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-ink text-white text-sm font-medium hover:opacity-90"
              >
                <Save size={13} /> Reset
              </button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
}
